import { useCallback, useEffect, useState } from 'react'

import { LocalStorageRegistry } from '@/constants/localStorageRegistry'
import { DEFAULT_RADIUS_AUTOS, RADIUS_SLIDER_HALF_POINT_KMS } from '@/constants/location'
import { didUserModifyRadius } from '@/domain/location/didUserModifyRadius'
import { getUserLocationFromSearchQuery } from '@/domain/location/getUserLocationFromSearchQuery'
import { setModifiedRadiusLocalStorage } from '@/domain/location/setModifiedRadiusLocalStorage'
import { isCarsVehiclesExtendedRadiusCategory } from '@/utils/category/categoryTree'

import { type UserLocation } from '../generated/graphql-types'
import { useGetLocation } from './location/useGetLocation'
import { useGetSearchResultsData } from './srp/useGetSearchResultsData'
import { useSearchActions } from './srp/useSearchActions'

/**
 * Decides if it should update the user's location radius based on the category ID
 * And if the user has updated their radius manually before
 */

type AutoUpdateRadiusValues = {
  currValue?: number
  prevValue?: number
  shouldShowSystemMessage: boolean
  didAutoUpdateRadius: boolean
}

type UseAutomaticallyUpdateRadiusResponse = {
  shouldShowSystemMessage: boolean
  autoUpdateRadiusValues?: Pick<AutoUpdateRadiusValues, 'prevValue' | 'currValue'>
  switchRadiusToPreviousValue: (prevRadius?: number) => Promise<void>
  handleCloseSystemMessage: () => void
}

export const useAutomaticallyUpdateRadius = (): UseAutomaticallyUpdateRadiusResponse => {
  const { updateUserLocation } = useGetLocation()

  const [didAutoUpdateRadius, setDidAutoUpdateRadius] = useState<boolean>(false)
  const [radiusValues, setRadiusValues] =
    useState<UseAutomaticallyUpdateRadiusResponse['autoUpdateRadiusValues']>()

  const { data, setLoadingStates } = useGetSearchResultsData()
  const { refetchResults } = useSearchActions()
  const category = data?.searchQuery?.category
  const location = getUserLocationFromSearchQuery(data?.searchQuery)

  const getLastRadiusValue = useCallback((): number => {
    const lastRadiusString = window.localStorage.getItem(
      LocalStorageRegistry.USER_LAST_RADIUS_VALUE
    )

    return lastRadiusString
      ? parseInt(lastRadiusString)
      : location.area?.radius || RADIUS_SLIDER_HALF_POINT_KMS
  }, [location.area?.radius])

  const handleUpdateRadius = async (newRadius: number) => {
    setLoadingStates({ results: true, filters: true })

    const newLocation: UserLocation = {
      id: location.id,
      isRegion: !location.area,
      name: location.name,
      area: location.area ? { ...location.area, radius: newRadius } : null,
    }

    await refetchResults({ location: newLocation })
    updateUserLocation(newLocation)

    const prevValue = getLastRadiusValue()
    setDidAutoUpdateRadius(true)

    setRadiusValues({ currValue: DEFAULT_RADIUS_AUTOS, prevValue })
  }

  const switchRadiusToPreviousValue = async (prevRadius?: number) => {
    setModifiedRadiusLocalStorage(true)
    await handleUpdateRadius(prevRadius || RADIUS_SLIDER_HALF_POINT_KMS)

    setDidAutoUpdateRadius(false)
  }

  const handleCloseSystemMessage = () => {
    setModifiedRadiusLocalStorage(true)
    setDidAutoUpdateRadius(false)
  }

  /**
   * TODO: Move this logic to the first call to get results in the page
   * We can have the information to show the system message saved on a cookie or something
   * and fetch the data just once instead of lading the user in one radius and search for another one
   * */
  useEffect(() => {
    /* If the category is not a vehicle, stop showing the banner
     **/
    if (category && !isCarsVehiclesExtendedRadiusCategory(category.id)) {
      setDidAutoUpdateRadius(false)
      return
    }

    /**
     * If the location is a region we want to skip this condition and remove the banner
     * */
    if (
      !location.isRegion &&
      (!location.area?.radius || location.area?.radius >= DEFAULT_RADIUS_AUTOS)
    )
      return

    /**
     * Shouldn't auto-update or show the system message if:
     * Category is still loading OR is not on Vehicles
     * Location is a region
     * There is no radius to be extended
     * */
    if (
      didUserModifyRadius() ||
      !category ||
      !isCarsVehiclesExtendedRadiusCategory(category.id) ||
      location.isRegion
    ) {
      setDidAutoUpdateRadius(false)
      return
    }

    handleUpdateRadius(DEFAULT_RADIUS_AUTOS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, getLastRadiusValue, location.isRegion, location.area?.radius])

  return {
    autoUpdateRadiusValues: radiusValues,
    shouldShowSystemMessage: didAutoUpdateRadius,
    switchRadiusToPreviousValue,
    handleCloseSystemMessage,
  }
}
